/**
 * Lyric Orchestrator Service
 * Core engine: 4 writers draft lyrics in parallel, synthesizer merges best sections.
 * Duplicates the proven callProvider pattern from orchestratorService.ts.
 */

import { generateText as geminiGenerateText } from '@/lib/gemini';
import { generateText as anthropicGenerateText } from '@/lib/anthropic';
import { trackUsage } from '@/utils/costTracker';
import {
  type LLMProvider,
  getProvider,
  getGeminiKey, getGeminiModel,
  getClaudeKey, getClaudeProxy, getClaudeModel,
  getOpenAIKey, getOpenAIModel,
  getXAIKey, getXAIModel,
} from './llmShared';
import { openaiCompatFetch } from '../utils/openaiProxyFetch';
import {
  LYRIC_WRITER_AGENTS,
  LYRIC_SYNTHESIZER_AGENT,
  DEFAULT_LYRIC_DELIBERATION_CONFIG,
} from '@/data/lyricOrchestratorAgents';
import type {
  LyricAgent,
  LyricDraftProposal,
  LyricDraftScores,
  LyricSynthesis,
  LyricDeliberationResult,
  LyricDeliberationConfig,
  LyricProgressEvent,
  LyricArchitectureContext,
  RawDraftResponse,
  RawSynthesisResponse,
  SectionAttribution,
} from '@/types/lyricOrchestrator.types';
import type { ParsedSunoOutput, HookhouseFormData } from '@/types';
import { SUNO_LYRICS_INJECTION_LINES } from '@/shared/sunoLyricsInjection';

// =============================================================================
// PROVIDER-AGNOSTIC LLM CALLER (duplicated from orchestratorService.ts)
// =============================================================================

interface LLMCallResult {
  text: string;
  inputTokens: number;
  outputTokens: number;
  provider: string;
  model: string;
}

async function callProvider(
  systemPrompt: string,
  userPrompt: string,
  provider?: LLMProvider,
  signal?: AbortSignal
): Promise<LLMCallResult> {
  const activeProvider = provider || getProvider();

  switch (activeProvider) {
    case 'gemini': return callGemini(systemPrompt, userPrompt, signal);
    case 'claude': return callClaude(systemPrompt, userPrompt, signal);
    case 'openai': return callOpenAI(systemPrompt, userPrompt, 'https://api.openai.com/v1/chat/completions', getOpenAIKey(), getOpenAIModel(), signal);
    case 'xai': return callOpenAI(systemPrompt, userPrompt, 'https://api.x.ai/v1/chat/completions', getXAIKey(), getXAIModel(), signal);
    default: throw new Error(`Unknown provider: ${activeProvider}`);
  }
}

async function callGemini(systemPrompt: string, userPrompt: string, signal?: AbortSignal): Promise<LLMCallResult> {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error('Gemini API key not configured.');
  const model = getGeminiModel();

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const { text, inputTokens, outputTokens } = await geminiGenerateText({
    apiKey, model, systemInstruction: systemPrompt, prompt: userPrompt, temperature: 0.9, signal,
  });

  trackUsage({ provider: 'gemini', model, feature: 'lyric-orchestrator', inputTokens, outputTokens });
  return { text, inputTokens, outputTokens, provider: 'gemini', model };
}

async function callClaude(systemPrompt: string, userPrompt: string, signal?: AbortSignal): Promise<LLMCallResult> {
  const apiKey = getClaudeKey();
  if (!apiKey) throw new Error('Claude API key not configured.');
  const model = getClaudeModel();

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const { text, inputTokens, outputTokens } = await anthropicGenerateText({
    apiKey, baseURL: getClaudeProxy(), model,
    systemPrompt, userPrompt, temperature: 0.9, maxTokens: 4096, signal,
  });

  trackUsage({ provider: 'claude', model, feature: 'lyric-orchestrator', inputTokens, outputTokens });
  return { text, inputTokens, outputTokens, provider: 'claude', model };
}

async function callOpenAI(
  systemPrompt: string, userPrompt: string,
  endpoint: string, apiKey: string, model: string,
  signal?: AbortSignal
): Promise<LLMCallResult> {
  if (!apiKey) throw new Error(`API key not configured for ${endpoint.includes('x.ai') ? 'xAI' : 'OpenAI'}.`);
  const providerName = endpoint.includes('x.ai') ? 'xai' : 'openai';

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const response = await openaiCompatFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      temperature: 0.9,
      max_tokens: 4096,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
    }),
    signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: { message?: string } }).error?.message || `${providerName} API error: ${response.status}`);
  }

  const data = await response.json() as {
    choices?: { message?: { content?: string } }[];
    usage?: { prompt_tokens?: number; completion_tokens?: number; cost_in_usd_ticks?: number };
  };

  const text = data.choices?.[0]?.message?.content || '';
  const inputTokens = data.usage?.prompt_tokens || 0;
  const outputTokens = data.usage?.completion_tokens || 0;
  const costTicks = providerName === 'xai' ? data.usage?.cost_in_usd_ticks : undefined;
  const actualCost = typeof costTicks === 'number' ? costTicks / 10_000_000_000 : undefined;

  trackUsage({ provider: providerName, model, feature: 'lyric-orchestrator', inputTokens, outputTokens, actualCost });
  return { text, inputTokens, outputTokens, provider: providerName, model };
}

// =============================================================================
// RESPONSE PARSING
// =============================================================================

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function extractJSON(text: string): string {
  let cleaned = text.trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '');

  const match = cleaned.match(/\{[\s\S]*\}/);
  if (!match) throw new Error('No JSON object found in response');
  return match[0];
}

/**
 * Normalize a score value: coerce strings to numbers, detect 0-1 range and scale to 0-10.
 */
function normalizeScore(val: unknown): number {
  let num: number;
  if (typeof val === 'number') {
    num = val;
  } else if (typeof val === 'string') {
    num = parseFloat(val);
    if (isNaN(num)) return 5; // fallback
  } else {
    return 5; // fallback
  }

  // Detect 0-1 range (all valid scores except 0 and 1 themselves)
  if (num > 0 && num < 1) {
    num = Math.round(num * 10);
  }

  return clamp(Math.round(num), 0, 10);
}

const DRAFT_SCORE_KEYS: (keyof LyricDraftScores)[] = [
  'structure', 'hook', 'imagery', 'authenticity', 'emotionalImpact', 'overall',
];

function isValidDraftScores(obj: unknown): obj is Record<string, unknown> {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  return DRAFT_SCORE_KEYS.every(k => o[k] !== undefined);
}

export function parseDraftResponse(text: string): RawDraftResponse {
  const jsonStr = extractJSON(text);
  let parsed: Record<string, unknown>;

  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    const repaired = jsonStr.replace(/,\s*([}\]])/g, '$1');
    parsed = JSON.parse(repaired);
  }

  if (!isValidDraftScores(parsed.scores)) {
    throw new Error('Invalid scores structure in draft response');
  }

  const rawScores = parsed.scores as Record<string, unknown>;
  const scores: LyricDraftScores = {
    structure: normalizeScore(rawScores.structure),
    hook: normalizeScore(rawScores.hook),
    imagery: normalizeScore(rawScores.imagery),
    authenticity: normalizeScore(rawScores.authenticity),
    emotionalImpact: normalizeScore(rawScores.emotionalImpact),
    overall: normalizeScore(rawScores.overall),
  };

  return {
    lyrics: typeof parsed.lyrics === 'string' ? parsed.lyrics : '',
    scores,
    strengths: Array.isArray(parsed.strengths) ? parsed.strengths.map(String) : [],
    weaknesses: Array.isArray(parsed.weaknesses) ? parsed.weaknesses.map(String) : [],
    confidence: clamp(typeof parsed.confidence === 'number' ? parsed.confidence : 0.5, 0, 1),
    approach: typeof parsed.approach === 'string' ? parsed.approach : '',
  };
}

export function parseSynthesisResponse(text: string): RawSynthesisResponse {
  const jsonStr = extractJSON(text);
  let parsed: Record<string, unknown>;

  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    const repaired = jsonStr.replace(/,\s*([}\]])/g, '$1');
    parsed = JSON.parse(repaired);
  }

  return {
    mergedLyrics: typeof parsed.mergedLyrics === 'string' ? parsed.mergedLyrics : '',
    title: typeof parsed.title === 'string' ? parsed.title : 'Untitled',
    styleTags: typeof parsed.styleTags === 'string' && parsed.styleTags.trim() ? parsed.styleTags.trim() : 'Southern Rock, Rock',
    excludedStyles: typeof parsed.excludedStyles === 'string' ? parsed.excludedStyles.trim() : '',
    summary: typeof parsed.summary === 'string' ? parsed.summary : '',
    attribution: Array.isArray(parsed.attribution)
      ? parsed.attribution.map((a: Record<string, unknown>) => ({
          section: String(a.section || ''),
          sourceAgentId: String(a.sourceAgentId || ''),
          modified: Boolean(a.modified),
        }))
      : [],
    mergeRationale: typeof parsed.mergeRationale === 'string' ? parsed.mergeRationale : '',
    overallAssessment: typeof parsed.overallAssessment === 'string' ? parsed.overallAssessment : '',
  };
}

// =============================================================================
// PROMPT BUILDING
// =============================================================================

function buildWriterPrompt(
  seed: string,
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
): string {
  const moodStr = moods.length > 0 ? moods.join(', ') : 'Dark, Gritty';

  // Resolve effective architectures (persona overrides take priority)
  const vocalArch = arch?.personaOverride?.vocalArchitecture || arch?.vocalArchitecture;
  const prodArch = arch?.personaOverride?.productionArchitecture || arch?.productionArchitecture;
  const bandIdentity = arch?.personaOverride?.bandIdentityBlock;

  let prompt = `Write a complete song based on this creative brief.

SONG CONCEPT / SEED:
${seed}

GENRE: ${genre}
MOOD: ${moodStr}`;

  // Include architecture context so agents write lyrics that fit the configured sound
  if (vocalArch) {
    prompt += `

VOCAL ARCHITECTURE (write lyrics that complement this vocal character):
${vocalArch}`;
  }

  if (prodArch) {
    prompt += `

PRODUCTION ARCHITECTURE (write lyrics that suit this instrumentation and mix):
${prodArch}`;
  }

  if (arch?.riffDnaBlock) {
    prompt += `

RIFF DNA (signature riff identity — write lyrics that complement this riff character):
${arch.riffDnaBlock}`;
  }

  if (arch?.soloBlock) {
    prompt += `

SOLO / INSTRUMENTAL SECTIONS:
${arch.soloBlock}`;
  }

  if (arch?.narrativeBlock) {
    prompt += `

NARRATIVE ENGINE (follow this story architecture):
${arch.narrativeBlock}`;
  }

  if (bandIdentity) {
    prompt += `

BAND IDENTITY:
${bandIdentity}`;
  }

  prompt += `

Write the full lyrics as YOUR best creative interpretation of this concept. Include section tags ([Verse 1], [Chorus], etc.).
Write SHORT, punchy stanzas — 4 lines per verse, 4 lines per chorus. No 8-line blocks. Keep it tight.
Your lyrics should harmonize with the vocal character, production palette, and narrative direction above.
The lyrics must be under 3500 characters total. This is a hard limit.
Remember: respond in JSON only.`;

  return prompt;
}

function buildSynthesisPrompt(
  drafts: LyricDraftProposal[],
  seed: string,
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
): string {
  const moodStr = moods.length > 0 ? moods.join(', ') : 'Dark, Gritty';

  let prompt = `[SONG CONTEXT]\n`;
  prompt += `SEED: "${seed}"\n`;
  prompt += `GENRE: ${genre}\n`;
  prompt += `MOOD: ${moodStr}\n`;

  // Include architecture context so synthesizer can generate rich style tags
  const vocalArch = arch?.personaOverride?.vocalArchitecture || arch?.vocalArchitecture;
  const prodArch = arch?.personaOverride?.productionArchitecture || arch?.productionArchitecture;
  if (vocalArch) {
    prompt += `\nVOCAL CHARACTER:\n${vocalArch}\n`;
  }
  if (prodArch) {
    prompt += `\nPRODUCTION ARCHITECTURE:\n${prodArch}\n`;
  }
  if (arch?.riffDnaBlock) {
    prompt += `\nRIFF DNA:\n${arch.riffDnaBlock}\n`;
  }
  if (arch?.narrativeBlock) {
    prompt += `\nNARRATIVE:\n${arch.narrativeBlock}\n`;
  }

  prompt += `\n[4 SPECIALIST LYRIC DRAFTS — MERGE THE BEST]\n\n`;
  prompt += `Below are 4 complete lyric drafts from different specialist writers, all written for the same song concept. `;
  prompt += `Read all 4 carefully, then merge the best sections into one superior, cohesive song.\n\n`;

  for (const draft of drafts) {
    prompt += `--- ${draft.agentName} [${draft.agentId}] (confidence: ${draft.confidence.toFixed(2)}, overall: ${draft.scores.overall}/10) ---
APPROACH: ${draft.approach}
STRENGTHS: ${draft.strengths.join(', ')}
WEAKNESSES: ${draft.weaknesses.join(', ')}

LYRICS:
${draft.lyrics}

`;
  }

  prompt += `\nMerge these into one cohesive song. Pick the BEST version of each section, maintaining narrative coherence and consistent voice. Attribute every section.`;
  prompt += `\nIMPORTANT: Keep stanzas SHORT — 4 lines per verse, 4 lines per chorus. No 8-line blocks. Tight and punchy.`;
  prompt += `\n\nFor styleTags: Generate DETAILED Suno-compatible style tags derived from the song context above. Include: genre, sub-genres, specific instruments, vocal character descriptors, tempo, key, production style, dynamics, era/influence references. Example density: "southern groove metal, heavy metal, southern rock, groove metal, 115 bpm, Bb major, gritty southern baritone, palm muted chugs, slide guitar, distorted bass, tight groove drums, saturated low mids, raw analog tape hiss". Be exhaustive — use the full 1000 character limit.`;
  prompt += `\nFor excludedStyles: Generate a DETAILED list of specific anti-tags that contradict the song's character. Include: wrong genres/sub-genres, wrong vocal styles, wrong instruments, wrong production aesthetics, wrong era/decade markers, wrong tempo feels, wrong emotional registers, wrong lyric tropes. Example density: "pop punk, boy band harmonies, autotuned vocals, bubblegum melodies, synth-pop leads, drum machines, glossy pop production, EDM buildups, generic dance beats, mumble rap flows, trap hi-hats, sub-bass drops, 4-on-the-floor kicks, ballad piano, orchestral swells, whistle notes, falsetto runs, cheesy love clichés". Be exhaustive — use the full 1000 character limit; Suno will discard tail overflow but 500-char excludes leave real filtering power on the table.`;

  return prompt;
}

// =============================================================================
// DRAFT EXECUTION
// =============================================================================

/** Execute a single agent draft with timeout + abort wiring. */
async function executeSingleDraft(
  agent: LyricAgent,
  userPrompt: string,
  config: LyricDeliberationConfig,
  signal?: AbortSignal,
): Promise<LyricDraftProposal> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const agentController = new AbortController();
  const timeout = setTimeout(() => agentController.abort(), config.perAgentTimeoutMs);
  const onParentAbort = () => agentController.abort();
  signal?.addEventListener('abort', onParentAbort);

  try {
    const { text, provider, model } = await callProvider(
      agent.systemPrompt, userPrompt, agent.preferredProvider, agentController.signal
    );
    const parsed = parseDraftResponse(text);

    return {
      agentId: agent.id,
      agentName: agent.name,
      lyrics: parsed.lyrics,
      scores: parsed.scores,
      strengths: parsed.strengths,
      weaknesses: parsed.weaknesses,
      confidence: parsed.confidence,
      approach: parsed.approach,
      provider,
      model,
    };
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', onParentAbort);
  }
}

async function executeDrafting(
  agents: LyricAgent[],
  seed: string,
  genre: string,
  moods: string[],
  config: LyricDeliberationConfig,
  arch?: LyricArchitectureContext,
  signal?: AbortSignal,
  onProgress?: (event: LyricProgressEvent) => void,
): Promise<{ drafts: LyricDraftProposal[]; failedAgents: string[]; failureReasons: Record<string, string> }> {
  const userPrompt = buildWriterPrompt(seed, genre, moods, arch);

  // Stagger agent starts by 300ms to reduce rate-limit pressure
  const promises = agents.map(async (agent, i): Promise<LyricDraftProposal> => {
    if (i > 0) await new Promise(r => setTimeout(r, i * 300));
    return executeSingleDraft(agent, userPrompt, config, signal);
  });

  const results = await Promise.allSettled(promises);
  const drafts: LyricDraftProposal[] = [];
  const failedAgentIds: string[] = [];
  const failureReasons: Record<string, string> = {};

  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      drafts.push(result.value);
      onProgress?.({ type: 'agent-complete', agentId: agents[i].id });
    } else {
      const agentId = agents[i].id;
      const reason = result.reason instanceof Error ? result.reason.message : 'Unknown error';
      failedAgentIds.push(agentId);
      failureReasons[agentId] = reason;
      console.warn(`⚠️ [LyricOrchestrator] Agent ${agentId} failed:`, result.reason);
      onProgress?.({ type: 'agent-failed', agentId, error: reason });
    }
  });

  // ── Auto-retry failed agents once (sequentially to avoid rate limits) ──
  if (failedAgentIds.length > 0) {
    console.log(`🔄 [LyricOrchestrator] Retrying ${failedAgentIds.length} failed agent(s): ${failedAgentIds.join(', ')}`);

    for (const agentId of [...failedAgentIds]) {
      if (signal?.aborted) break;

      const agent = agents.find(a => a.id === agentId);
      if (!agent) continue;

      await new Promise(r => setTimeout(r, 1000));

      try {
        const draft = await executeSingleDraft(agent, userPrompt, config, signal);
        drafts.push(draft);
        const idx = failedAgentIds.indexOf(agentId);
        if (idx >= 0) failedAgentIds.splice(idx, 1);
        delete failureReasons[agentId];
        console.log(`✅ [LyricOrchestrator] Retry succeeded for ${agentId}`);
        onProgress?.({ type: 'agent-complete', agentId });
      } catch (retryErr) {
        const reason = retryErr instanceof Error ? retryErr.message : 'Unknown error';
        failureReasons[agentId] = `Retry failed: ${reason}`;
        console.warn(`⚠️ [LyricOrchestrator] Retry also failed for ${agentId}:`, retryErr);
      }
    }
  }

  return { drafts, failedAgents: failedAgentIds, failureReasons };
}

// =============================================================================
// SYNTHESIS
// =============================================================================

async function synthesize(
  drafts: LyricDraftProposal[],
  seed: string,
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
  signal?: AbortSignal,
): Promise<LyricSynthesis> {
  // Stable ordering: sort by canonical agent list order
  const agentOrder = LYRIC_WRITER_AGENTS.map(a => a.id);
  const sortedDrafts = [...drafts].sort(
    (a, b) => agentOrder.indexOf(a.agentId) - agentOrder.indexOf(b.agentId)
  );

  const prompt = buildSynthesisPrompt(sortedDrafts, seed, genre, moods, arch);
  const { text } = await callProvider(
    LYRIC_SYNTHESIZER_AGENT.systemPrompt, prompt, undefined, signal
  );

  const parsed = parseSynthesisResponse(text);

  return {
    mergedLyrics: parsed.mergedLyrics,
    title: parsed.title,
    styleTags: parsed.styleTags.substring(0, 1000).trim() || 'Southern Rock, Rock',
    excludedStyles: parsed.excludedStyles.substring(0, 1000).trim(),
    summary: parsed.summary,
    attribution: parsed.attribution,
    mergeRationale: parsed.mergeRationale,
    overallAssessment: parsed.overallAssessment,
  };
}

// =============================================================================
// MAIN ENTRY POINT
// =============================================================================

export async function runLyricDeliberation(
  seed: string,
  genre: string,
  moods: string[],
  arch?: LyricArchitectureContext,
  config?: Partial<LyricDeliberationConfig>,
  signal?: AbortSignal,
  onProgress?: (event: LyricProgressEvent) => void,
): Promise<LyricDeliberationResult> {
  const fullConfig: LyricDeliberationConfig = { ...DEFAULT_LYRIC_DELIBERATION_CONFIG, ...config };
  const agents = LYRIC_WRITER_AGENTS;
  const startTime = Date.now();

  // Total timeout guard
  const totalController = new AbortController();
  const totalTimeout = setTimeout(() => totalController.abort(), fullConfig.totalTimeoutMs);
  const onParentAbort = () => totalController.abort();
  signal?.addEventListener('abort', onParentAbort);

  try {
    // ── Drafting ──
    onProgress?.({ type: 'drafting-start' });

    const { drafts, failedAgents, failureReasons } = await executeDrafting(
      agents, seed, genre, moods, fullConfig, arch, totalController.signal, onProgress
    );

    onProgress?.({ type: 'drafting-complete', successCount: drafts.length });

    // Terminal branch: not enough agents completed
    if (drafts.length < fullConfig.minAgentsRequired) {
      const reasonDetails = Object.entries(failureReasons)
        .map(([id, reason]) => `${id}: ${reason}`)
        .join('; ');
      throw new Error(
        `Only ${drafts.length} of ${agents.length} agents completed. ` +
        `Minimum ${fullConfig.minAgentsRequired} required. ` +
        `Failed: ${reasonDetails || failedAgents.join(', ')}`
      );
    }

    // ── Synthesis ──
    onProgress?.({ type: 'synthesis-start' });

    const synthesis = await synthesize(drafts, seed, genre, moods, arch, totalController.signal);

    onProgress?.({ type: 'synthesis-complete' });

    return {
      id: `lyric_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      drafts,
      synthesis,
      totalDurationMs: Date.now() - startTime,
      agentCount: agents.length,
      failedAgents,
    };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    onProgress?.({ type: 'error', message: err instanceof Error ? err.message : 'Lyric deliberation failed' });
    throw err;
  } finally {
    clearTimeout(totalTimeout);
    signal?.removeEventListener('abort', onParentAbort);
  }
}

// =============================================================================
// BRIDGE — Convert lyric orchestrator result to ParsedSunoOutput
// =============================================================================

export function toLyricOutput(
  result: LyricDeliberationResult,
  formData: HookhouseFormData,
  vocalArchitecture?: string,
): ParsedSunoOutput {
  const s = result.synthesis;
  const rawLyrics = s.mergedLyrics || '';
  const styleTags = (s.styleTags && s.styleTags.trim()) ? s.styleTags.trim().substring(0, 1000) : 'Southern Rock, Rock';
  const excludedStyles = (s.excludedStyles && s.excludedStyles.trim()) ? s.excludedStyles.trim().substring(0, 1000) : '';
  const title = s.title || 'Untitled';

  // Extract vocal physics blocks for Suno — skip metadata (META_STYLES, MIX_TARGETS,
  // PACING, DESCRIPTION, etc.). Include LAYER-1 + scream + deep-dive, but condense
  // deep-dive to one line per sub-block (header + first [Tag]) to keep it tight.
  let physicsBlock = '';
  if (vocalArchitecture) {
    const sections: string[] = [];

    // Core vocal physics (always present)
    const layer1 = vocalArchitecture.match(/\{LAYER-1 VOCAL PHYSICS\}\n(?:\[.*\]\n?)*/);
    if (layer1) sections.push(layer1[0].trim());

    // Scream physics (if configured)
    const scream = vocalArchitecture.match(/\{SCREAM-PHYSICS:.*\}\n(?:\[.*\]\n?)*/);
    if (scream) sections.push(scream[0].trim());

    // Deep-dive: condense each sub-block to header + first tag line only
    const ddBlocks = [...vocalArchitecture.matchAll(
      /(\{(?:DIALECT|AGING|BREATH|ARC)-PHYSICS:.*\})\n(\[.*\])/g
    )];
    if (ddBlocks.length > 0) {
      sections.push(
        '{DEEP-DIVE VOCAL LAYERS}\n' +
        ddBlocks.map(m => `${m[1]}\n${m[2]}`).join('\n')
      );
    }

    physicsBlock = sections.join('\n\n');
  }
  const lyrics = physicsBlock
    ? `${physicsBlock}\n\n${rawLyrics}`
    : rawLyrics;

  const lyricsAlone = rawLyrics
    .replace(/\[.*?\]\n?/g, '')
    .trim();

  // Build console injection code (same pattern as llmShared.ts)
  const machineStyle = styleTags.substring(0, 1000).trim();
  const machineLyrics = lyrics.substring(0, 5000).trim();
  const weirdness = formData.weirdness ?? 50;
  const styleStrength = formData.styleStrength ?? 50;

  const javascriptCode = [
    '// --- Hookhouse Pro (Parliament Write) Terminal ---',
    '// Paste this while on suno.com/create in "Custom Mode"',
    '',
    'function setNativeValue(element, value) {',
    '    if (!element) return;',
    '    const valueSetter = Object.getOwnPropertyDescriptor(element.constructor.prototype, "value")?.set;',
    '    const prototypeValueSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), "value")?.set;',
    '    if (valueSetter && valueSetter !== prototypeValueSetter) {',
    '        valueSetter.call(element, value);',
    '    } else if (valueSetter) {',
    '        valueSetter.call(element, value);',
    '    }',
    '    element.dispatchEvent(new Event("input", { bubbles: true }));',
    '}',
    '',
    ...SUNO_LYRICS_INJECTION_LINES,
    '',
    'function adjustSlider(sliderLabel, targetValue) {',
    '    const slider = document.querySelector(`div[role="slider"][aria-label*="${sliderLabel}"]`);',
    '    if (slider) {',
    '        slider.focus();',
    '        const pressKey = (key) => slider.dispatchEvent(new KeyboardEvent("keydown", { key, code: key, bubbles: true, cancelable: true }));',
    '        const intervalId = setInterval(() => {',
    '            const currentVal = parseFloat(slider.getAttribute("aria-valuenow"));',
    '            if (Math.abs(currentVal - targetValue) < 1) { clearInterval(intervalId); return; }',
    '            if (currentVal > targetValue) pressKey("ArrowLeft"); else if (currentVal < targetValue) pressKey("ArrowRight");',
    '        }, 10);',
    '    }',
    '}',
    '',
    'console.log("\\uD83D\\uDE80 Injecting Parliament Write Architecture...");',
    '',
    'injectLyrics(' + JSON.stringify(machineLyrics) + ');',
    '',
    'const styleInput = document.querySelector(\'input[placeholder*="style of music"]\');',
    'if (styleInput) { setNativeValue(styleInput, ' + JSON.stringify(machineStyle) + '); console.log("\\u2713 Style tags injected"); }',
    '',
    'const songTitleInput = document.querySelector(\'input[placeholder*="Song Title"]\');',
    'if (songTitleInput) { setNativeValue(songTitleInput, ' + JSON.stringify(title) + '); console.log("\\u2713 Title injected"); }',
    '',
    'adjustSlider("Weirdness", ' + weirdness + ');',
    'adjustSlider("Style Influence", ' + styleStrength + ');',
    '',
    'console.log("%c Parliament Write Injection Complete! ", "background: rgb(var(--color-accent)); color: #000; font-size: 14px; font-weight: bold;");',
  ].join('\n');

  const styleLen = styleTags.length;
  const excludeLen = excludedStyles.length;
  const lyricsLen = lyrics.length;

  return {
    titleArtist: `${title} — Parliament Writers`,
    summary: s.summary,
    styleBlueprint: styleTags,
    style: styleTags,
    excludedStyle: excludedStyles,
    title,
    artist: 'Parliament Writers',
    lyricsWithTags: lyrics,
    lyricsAlone,
    javascriptCode,
    fullResponse: JSON.stringify(result, null, 2),
    styleStrength,
    weirdness,
    llmModel: result.drafts[0]?.model || 'unknown',
    llmProvider: result.drafts[0]?.provider || 'unknown',
    charLimits: {
      styleTags: { count: styleLen, max: 1000, over: styleLen > 1000 },
      excludedStyles: { count: excludeLen, max: 1000, over: excludeLen > 1000 },
      lyrics: { count: lyricsLen, max: 5000, over: lyricsLen > 5000 },
    },
  };
}
